import { useState } from 'react';
import { MdClose, MdMic, MdKeyboard } from 'react-icons/md';

export type LessonMode = 'voice' | 'text';

interface ModeSelectionModalProps {
  onSelectMode: (mode: LessonMode) => void;
  onCancel: () => void;
  audioAvailable?: boolean;
  defaultMode?: LessonMode | null;
}

const CloseIcon = MdClose as any;
const MicIcon = MdMic as any;
const KeyboardIcon = MdKeyboard as any;

export default function ModeSelectionModal({
  onSelectMode,
  onCancel,
  audioAvailable = true,
  defaultMode,
}: ModeSelectionModalProps) {
  const [selectedMode, setSelectedMode] = useState<LessonMode>(defaultMode || 'voice');

  const handleContinue = () => {
    onSelectMode(selectedMode);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#111827] rounded-lg p-4 md:p-8 max-w-sm md:max-w-lg w-full border border-gray-600 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onCancel}
          className="absolute top-3 right-3 md:top-4 md:right-4 text-gray-400 hover:text-white transition-colors"
          aria-label="Close"
        >
          <CloseIcon className="w-5 h-5 md:w-6 md:h-6" />
        </button>

        <h2 className="text-white text-lg md:text-2xl font-bold mb-2 md:mb-4 text-center pr-8">
          Choose Your Learning Mode
        </h2>
        <p className="text-gray-300 text-xs md:text-base text-center mb-4 md:mb-6">
          Select how you'd like to interact with your AI tutor
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-4 md:mb-6">
          {/* Voice Mode */}
          <button
            onClick={() => setSelectedMode('voice')}
            disabled={!audioAvailable}
            className={`
              p-4 md:p-6 rounded-lg border-2 transition-all
              ${selectedMode === 'voice'
                ? 'border-primary bg-primary/10'
                : 'border-gray-600 hover:border-gray-500'
              }
              ${!audioAvailable ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
          >
            <div className="flex flex-col items-center text-center">
              <MicIcon className={`w-8 h-8 md:w-12 md:h-12 mb-2 md:mb-3 ${selectedMode === 'voice' ? 'text-primary' : 'text-gray-400'}`} />
              <h3 className="text-white font-bold text-base md:text-lg mb-1 md:mb-2">Voice Mode</h3>
              <p className="text-gray-400 text-xs md:text-sm">
                Speak naturally with the AI tutor
              </p>
              {defaultMode === 'voice' && (
                <div className="mt-2 md:mt-3 px-2 md:px-3 py-1 bg-primary/20 text-primary text-xs rounded-full">
                  Your default
                </div>
              )}
              {!audioAvailable && (
                <div className="mt-2 md:mt-3 px-2 md:px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                  Audio Unavailable
                </div>
              )}
            </div>
          </button>

          {/* Text Mode */}
          <button
            onClick={() => setSelectedMode('text')}
            className={`
              p-4 md:p-6 rounded-lg border-2 transition-all cursor-pointer
              ${selectedMode === 'text'
                ? 'border-primary bg-primary/10'
                : 'border-gray-600 hover:border-gray-500'
              }
            `}
          >
            <div className="flex flex-col items-center text-center">
              <KeyboardIcon className={`w-8 h-8 md:w-12 md:h-12 mb-2 md:mb-3 ${selectedMode === 'text' ? 'text-primary' : 'text-gray-400'}`} />
              <h3 className="text-white font-bold text-base md:text-lg mb-1 md:mb-2">Text Mode</h3>
              <p className="text-gray-400 text-xs md:text-sm">
                Type your questions like a chat
              </p>
              {defaultMode === 'text' && (
                <div className="mt-2 md:mt-3 px-2 md:px-3 py-1 bg-primary/20 text-primary text-xs rounded-full">
                  Your default
                </div>
              )}
            </div>
          </button>
        </div>

        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
          <p className="text-blue-300 text-xs md:text-sm">
            💡 <strong>Tip:</strong> Both modes support image uploads!
          </p>
        </div>

        <div className="flex gap-2 md:gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 md:px-6 py-2 md:py-3 bg-transparent border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm md:text-base"
          >
            Cancel
          </button>
          <button
            onClick={handleContinue}
            className="px-4 md:px-6 py-2 md:py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm md:text-base"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
